import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Cpu, Brain, Lock, Glasses, Link as LinkIcon, Bot, Satellite, Network, Atom } from "lucide-react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { STACK, FOCUS_AUDIENCES, SDGS } from "@/data/content";
import PageHero from "@/components/site/PageHero";
import KiteDiagram from "@/components/site/KiteDiagram";
import CTABand from "@/components/site/CTABand";

const ICONS = { brain: Brain, lock: Lock, glasses: Glasses, link: LinkIcon, bot: Bot, satellite: Satellite, network: Network, atom: Atom, cpu: Cpu };

export default function Focus() {
  useDocumentTitle("Our Focus | AI at the cornerstone, governance at the core — Meta61");
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 150);
    }
  }, [location.hash]);
  return (
    <div>
      <PageHero
        title="AI at the cornerstone."
        accent="Governance at the core."
        subhead="Meta61 advises on the technologies reshaping our world — and governs them at the same time, through one interdependent lens."
      />

      {/* Interdependent model */}
      <section className="m61-section">
        <div className="m61-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4"><p className="m61-eyebrow mb-5">The interdependent model</p>
            <h2 className="m61-h2">Two angles, one decision.</h2></div>
          <div className="lg:col-span-8 text-lg text-m61-muted leading-relaxed">
            Technology and governance are usually treated as separate disciplines handled by
            separate firms. We reject that split. A model's architecture and its accountability,
            a network's design and its sovereignty, a system's capability and its safety — these are
            the same decision seen from two angles. Meta61 holds both at once. That is what we mean
            by convergence.
          </div>
        </div>
      </section>

      {/* KITE */}
      <section className="m61-section border-t border-m61-line bg-m61-surface/40">
        <div className="m61-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="max-w-md mx-auto"><KiteDiagram /></div>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <p className="m61-eyebrow mb-5">Our operating model</p>
            <h2 className="m61-h2 mb-5">KITE — orchestrating digital and AI transformation.</h2>
            <p className="text-m61-muted leading-relaxed mb-6">
              KITE is Meta61's abstraction framework for digital and AI orchestration — the lens we
              bring to every engagement to drive resilience, sustainability, growth, and prosperity.
              It reduces the complexity of any AI or digital initiative to four interdependent
              dimensions, keeping the <em>why</em> of the work in view, not just the how.
            </p>
            <ul className="space-y-2 text-sm text-m61-muted mb-6">
              <li><span className="text-m61-text font-medium">AI</span> (top) — the technology and its capabilities.</li>
              <li><span className="text-m61-text font-medium">Organisation</span> (left) — the enterprise, government, or institution.</li>
              <li><span className="text-m61-text font-medium">Society</span> (right) — stakeholders, communities, and the public good.</li>
              <li><span className="text-m61-text font-medium">Sustainability</span> (bottom) — the SDGs, the environment, and long-term resilience.</li>
            </ul>
            <p className="text-xs text-m61-muted/80 font-mono">
              Provenance: KITE was introduced at the UN World Data Forum (2021) and is applied by Meta61
              as its orchestration operating model.
            </p>
          </div>
        </div>
      </section>

      {/* Stack in depth */}
      <section className="m61-section">
        <div className="m61-container">
          <p className="m61-eyebrow mb-10">The convergence stack — in depth</p>
          <div className="space-y-px bg-m61-line border border-m61-line">
            {STACK.map((s) => {
              const Icon = ICONS[s.icon] || Cpu;
              return (
                <div key={s.name} className="bg-m61-surface p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                  <div className="lg:col-span-4 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-m61-surface2 border border-m61-line flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-m61-cyan" />
                    </div>
                    <div>
                      <h3 className="m61-h3">{s.name}</h3>
                      {s.tag && <span className="font-mono text-[0.6rem] uppercase tracking-wider text-m61-violet">{s.tag}</span>}
                    </div>
                  </div>
                  <div className="lg:col-span-8">
                    <p className="text-m61-muted leading-relaxed mb-3">{s.short}</p>
                    <p className="text-sm text-m61-cyan/90"><span className="text-m61-muted">Governance dimension:</span> {s.governance}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SDGs */}
      <section id="sdgs" className="m61-section border-t border-m61-line scroll-mt-24">
        <div className="m61-container">
          <div className="max-w-2xl mb-12">
            <p className="m61-eyebrow mb-5">Aligned to the UN SDGs</p>
            <h2 className="m61-h2 mb-4">Technology directed toward the global goals.</h2>
            <p className="m61-lead">
              AI for Good runs through everything we do. KITE points our work at all 17 UN Sustainable
              Development Goals — and these are the goals most closely tied to Meta61's focus.
            </p>
          </div>

          {/* Key goals with rationale */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-m61-line border border-m61-line mb-12">
            {SDGS.filter((s) => s.key).map((s) => (
              <div key={s.num} className="bg-m61-surface p-6 hover:bg-m61-surface2 transition-colors">
                <img
                  src={`/sdg/${s.num}.png`}
                  alt={`UN SDG ${s.num}: ${s.title}`}
                  className="w-16 h-16 rounded-md mb-4"
                  loading="lazy"
                />
                <p className="text-m61-muted text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          {/* Full 17 */}
          <p className="m61-label mb-5">We support all 17 goals</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-3">
            {SDGS.map((s) => (
              <img
                key={s.num}
                src={`/sdg/${s.num}.png`}
                alt={`UN SDG ${s.num}: ${s.title}`}
                title={`SDG ${s.num} — ${s.title}`}
                loading="lazy"
                className={`w-full rounded-md transition-transform duration-300 hover:-translate-y-1 ${s.key ? "ring-2 ring-offset-2 ring-offset-m61-bg ring-m61-cyan" : "opacity-50 hover:opacity-100"}`}
              />
            ))}
          </div>
          <p className="mt-6 text-sm text-m61-muted">
            Highlighted goals are those most closely tied to Meta61's focus. The Sustainable
            Development Goals and their icons are the property of the United Nations.
          </p>
        </div>
      </section>

      {/* Who we work with */}
      <section className="m61-section border-t border-m61-line">
        <div className="m61-container">
          <p className="m61-eyebrow mb-10">Who we work with</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {FOCUS_AUDIENCES.map((a) => (
              <div key={a.title} className="m61-card p-8">
                <h3 className="m61-h3 mb-3">{a.title}</h3>
                <p className="text-m61-muted leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The name */}
      <section className="m61-section border-t border-m61-line bg-m61-surface/40">
        <div className="m61-container max-w-3xl">
          <p className="m61-eyebrow mb-5">The name</p>
          <p className="text-2xl lg:text-3xl font-display text-m61-text leading-snug">
            <span className="m61-gradient-text">61.</span> The mark of our Australian identity.
            Meta61 is built in Australia — strengthening national capability at home, and contributing
            to standards with the world.
          </p>
        </div>
      </section>

      <CTABand showAssessment />
    </div>
  );
}
